import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API, authFetch } from "@/App";
import { Users, Loader2, AlertTriangle, CheckCircle, TrendingUp, Target } from "lucide-react";

const STATUS_CONFIG = {
  highly_engaged: { label: "Engaged", color: "bg-green-100 text-green-700", dot: "bg-green-500" },
  active: { label: "Active", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  at_risk: { label: "At Risk", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  disengaged: { label: "Disengaged", color: "bg-red-100 text-red-700", dot: "bg-red-500" },
};

export function ActivityScoresPanel({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) fetchScores();
  }, [open]);

  const fetchScores = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/consultant/activity-scores`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="activity-scores-panel">
        <DialogHeader className="p-5 border-b">
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#2BC40E]" /> Client Activity Scores
          </DialogTitle>
          <DialogDescription>Engagement health across all clients — sorted by risk level</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" /></div>
          ) : data ? (
            <>
              {/* Stats */}
              <div className="grid grid-cols-4 gap-3 mb-6">
                {[
                  { label: "Engaged", value: data.stats.highly_engaged, color: "text-green-600", bg: "bg-green-50" },
                  { label: "Active", value: data.stats.active, color: "text-blue-600", bg: "bg-blue-50" },
                  { label: "At Risk", value: data.stats.at_risk, color: "text-amber-600", bg: "bg-amber-50" },
                  { label: "Disengaged", value: data.stats.disengaged, color: "text-red-600", bg: "bg-red-50" },
                ].map(s => (
                  <Card key={s.label} className="border-neutral-200">
                    <CardContent className={`p-4 text-center ${s.bg}`}>
                      <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-neutral-500">{s.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Client List */}
              <div className="space-y-2">
                {data.clients.map(c => {
                  const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.active;
                  return (
                    <div key={c.user_id} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-100">
                      {/* Score Circle */}
                      <div className="relative w-11 h-11 flex-shrink-0">
                        <svg className="w-11 h-11 -rotate-90" viewBox="0 0 36 36">
                          <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                          <circle cx="18" cy="18" r="15.5" fill="none" stroke={c.score >= 70 ? "#22c55e" : c.score >= 40 ? "#3b82f6" : c.score >= 20 ? "#f59e0b" : "#ef4444"}
                            strokeWidth="3" strokeDasharray={`${c.score} ${100 - c.score}`} strokeLinecap="round" />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-neutral-700">{c.score}</span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm text-neutral-900 truncate">{c.name}</p>
                          <Badge className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>
                          <Badge variant="outline" className="text-[10px]">{c.tier}</Badge>
                        </div>
                        <p className="text-xs text-neutral-500 truncate">{c.email}</p>
                      </div>

                      <div className="text-right text-xs text-neutral-500 flex-shrink-0 space-y-0.5">
                        <p>Login: {c.details?.login || "?"}</p>
                        <p>{c.details?.tools_used || 0} tools · {c.details?.logs_30d || 0} logs</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-neutral-500 text-center py-12">No data available</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ActivityScoresPanel;
